import { setFilter, setPeriod, setSearch, setSort } from './state/actions';
import { reducer } from './state/reducer';
import { createInitialHoldersState } from './state/types';
import type {
    HolderFilter,
    HolderSortColumn,
    HoldersPeriodId,
    HoldersState,
    InstitutionalHolderPeriod,
    InstitutionalHolderViewRow,
    InstitutionalHoldersSummary,
    InstitutionalHoldersSummaryView,
} from './state/types';
import { DateTimeFormatter, ZoneId, ZonedDateTime } from '@js-joda/core';
import type { Instant, LocalDate } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type {
    InstitutionalHolder,
    InstitutionalHoldersResponse,
    InstitutionalHoldersSortBy,
} from '@thinkalpha/quant-ws-client/contracts/institutional-holders.js';
import { ONE_MINUTE } from '@thinkalpha/ui-core/util/timeConstants';
import numbro from 'numbro';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getInstitutionalHoldersQuery } from 'src/queries/institutionalHolders';
import type { ReactBindings } from 'src/types/bindings';

const EMPTY_ROWS: readonly InstitutionalHolderViewRow[] = Object.freeze([]);
const HOLDERS_LIMIT = 100;
const SUMMARY_CACHE_TIME = ONE_MINUTE * 30;
const NEW_YORK = ZoneId.of('America/New_York');
const AS_OF_DATE_FORMATTER = DateTimeFormatter.ofPattern('MMM dd, yyyy').withLocale(Locale.ENGLISH);
const UPDATED_AT_FORMATTER = DateTimeFormatter.ofPattern('MMM dd, yyyy - HH:mm').withLocale(Locale.ENGLISH);

const EMPTY_SUMMARY: InstitutionalHoldersSummary = Object.freeze({
    institutionalOwnershipPct: 0,
    totalHolders: 0,
    newPositions: 0,
    netChange: 0,
    closedPositions: 0,
    asOfDate: null,
    updatedAt: null,
    hasDiff: false,
});

const STATUS_LABELS: Readonly<Record<InstitutionalHolder['status'], string>> = Object.freeze({
    increased: 'Increased',
    reduced: 'Reduced',
    new: 'New',
    closed: 'Closed',
});

function formatPercentFraction(value: number, mantissa = 1): string {
    return numbro(value).format({ output: 'percent', mantissa });
}

function formatSignedPercentFraction(value: number, mantissa = 1): string {
    if (value === 0) return '0.0%';
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${formatPercentFraction(value, mantissa)}`;
}

function formatCompactNumber(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) {
        return `${numbro(value / 1_000_000_000).format({ mantissa: 1, optionalMantissa: true })}B`;
    }
    if (abs >= 1_000_000) {
        return `${numbro(value / 1_000_000).format({ mantissa: 1, optionalMantissa: true })}M`;
    }
    if (abs >= 1_000) {
        return `${numbro(value / 1_000).format({ mantissa: 1, optionalMantissa: true })}K`;
    }
    return numbro(value).format({ thousandSeparated: true });
}

function formatCompactCurrency(value: number): string {
    if (value === 0) return '$0';
    return `$${formatCompactNumber(value)}`;
}

function formatSignedCompactNumber(value: number): string {
    if (value === 0) return '0';
    const prefix = value > 0 ? '+' : '-';
    return `${prefix}${formatCompactNumber(Math.abs(value))}`;
}

function formatLocalDate(value: LocalDate | null): string {
    if (value === null) return 'Latest';
    return value.format(AS_OF_DATE_FORMATTER);
}

function formatUpdatedAt(value: Instant | null, fallback: LocalDate | null): string {
    if (value === null) return formatLocalDate(fallback);
    return `${ZonedDateTime.ofInstant(value, NEW_YORK).format(UPDATED_AT_FORMATTER)} ET`;
}

function sortByForColumn(column: HolderSortColumn): InstitutionalHoldersSortBy {
    switch (column) {
        case 'rank':
        case 'shares':
        case 'value':
            return 'holdingAmount';
        case 'holder':
            return 'fund';
        case 'ownershipPct':
            return 'pctSharesOutstanding';
        case 'floatPct':
            return 'pctSharesFloat';
        case 'changeShares':
            return 'changeAmount12m';
        case 'changePct':
            return 'changePct12m';
        default: {
            const _exhaustiveCheck: never = column;
            return _exhaustiveCheck;
        }
    }
}

function toSummary(response: InstitutionalHoldersResponse | undefined): InstitutionalHoldersSummary {
    if (response === undefined) return EMPTY_SUMMARY;
    return {
        institutionalOwnershipPct: response.kpis.totalPctOutstanding,
        totalHolders: response.kpis.holdingsCount,
        newPositions: response.kpis.countNew,
        netChange: response.kpis.netChange,
        closedPositions: response.kpis.countClosed,
        asOfDate: response.asOfDate,
        updatedAt: response.updatedAt ?? null,
        hasDiff: response.kpis.hasDiff,
    };
}

function getInstitutionalHoldersSummaryCacheKey(symbol: string): readonly unknown[] {
    return ['institutional-holders-summary', symbol, 'latest'] as const;
}

function toViewRow(
    row: InstitutionalHolder,
    rank: number,
    maxOwnershipPct: number,
    lastPrice: number | null,
): InstitutionalHolderViewRow {
    const ownershipPct = row.pctSharesOutstanding;
    const floatPct = row.pctSharesFloat;
    const valueUsd = lastPrice === null ? null : row.holdingAmount * lastPrice;
    const changeDirection = row.changeAmount12m > 0 ? 'up' : row.changeAmount12m < 0 ? 'down' : 'flat';
    return {
        id: `${rank}:${row.fund}`,
        rank,
        name: row.fund,
        shares: row.holdingAmount,
        valueUsd,
        ownershipPct,
        floatPct,
        changeShares: row.changeAmount12m,
        changePct: row.changePct12m,
        status: row.status,
        sharesFormatted: formatCompactNumber(row.holdingAmount),
        valueFormatted: valueUsd === null ? '-' : formatCompactCurrency(valueUsd),
        ownershipFormatted: formatPercentFraction(row.pctSharesOutstanding, 2),
        floatPctFormatted: formatPercentFraction(row.pctSharesFloat, 2),
        ownershipBarPct: maxOwnershipPct > 0 ? Math.max(4, (ownershipPct / maxOwnershipPct) * 100) : 0,
        changeFormatted: formatSignedCompactNumber(row.changeAmount12m),
        changePctFormatted: formatSignedPercentFraction(row.changePct12m, 1),
        statusLabel: STATUS_LABELS[row.status],
        changeDirection,
    };
}

function toSummaryView(summary: InstitutionalHoldersSummary): InstitutionalHoldersSummaryView {
    return {
        ...summary,
        institutionalOwnershipFormatted: formatPercentFraction(summary.institutionalOwnershipPct, 1),
        totalHoldersFormatted: numbro(summary.totalHolders).format({ thousandSeparated: true }),
        newPositionsFormatted: `+${numbro(summary.newPositions).format({ thousandSeparated: true })}`,
        netChangeFormatted: formatSignedCompactNumber(summary.netChange),
        closedPositionsFormatted: `-${numbro(summary.closedPositions).format({ thousandSeparated: true })}`,
        asOfFormatted: formatLocalDate(summary.asOfDate),
        updatedAtFormatted: formatUpdatedAt(summary.updatedAt, summary.asOfDate),
    };
}

export interface HoldersModelInit {
    symbol?: ReadonlySignal<string>;
    enabled?: ReadonlySignal<boolean>;
    lastPrice?: ReadonlySignal<number | null>;
}

export class HoldersModel implements Disposable {
    readonly #state = signal<HoldersState>(createInitialHoldersState());
    readonly #symbol: ReadonlySignal<string>;
    readonly #enabled: ReadonlySignal<boolean>;
    readonly #lastPrice: ReadonlySignal<number | null>;
    readonly #holdersQuery: QueryObserverModel<InstitutionalHoldersResponse, Error, InstitutionalHoldersResponse>;
    readonly #summaryQuery: QueryObserverModel<InstitutionalHoldersSummary, Error, InstitutionalHoldersSummary>;
    readonly #disposableStack = new DisposableStack();

    readonly #periods: ReadonlySignal<readonly InstitutionalHolderPeriod[]>;
    readonly #periodId = computed(() => this.#state.value.periodId);
    readonly #filter = computed(() => this.#state.value.filter);
    readonly #search = computed(() => this.#state.value.search);
    readonly #sortColumn = computed(() => this.#state.value.sortColumn);
    readonly #sortDirection = computed(() => this.#state.value.sortDirection);
    readonly #loading: ReadonlySignal<boolean>;
    readonly #summaryLoading: ReadonlySignal<boolean>;
    readonly #error: ReadonlySignal<string | null>;
    readonly #summary: ReadonlySignal<InstitutionalHoldersSummaryView>;
    readonly #rows: ReadonlySignal<readonly InstitutionalHolderViewRow[]>;
    readonly #isEmpty: ReadonlySignal<boolean>;

    constructor(
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        queryClient: ReactBindings['QueryClient'],
        init: HoldersModelInit = {},
    ) {
        this.#symbol = init.symbol ?? signal('');
        this.#enabled = init.enabled ?? signal(false);
        this.#lastPrice = init.lastPrice ?? signal(null);

        const queryOptions = computed(() => {
            const state = this.#state.value;
            return getInstitutionalHoldersQuery({
                symbol: this.#symbol.value,
                enabled: this.#enabled.value,
                status: state.filter === 'all' ? undefined : state.filter,
                search: state.search,
                limit: HOLDERS_LIMIT,
                skip: 0,
                sortBy: sortByForColumn(state.sortColumn),
                sortOrder: state.sortDirection,
            });
        });

        this.#holdersQuery = queryObserverModelFactory<
            InstitutionalHoldersResponse,
            Error,
            InstitutionalHoldersResponse
        >(queryOptions);
        this.#disposableStack.use(this.#holdersQuery);

        const summaryQueryOptions = computed<
            QueryObserverOptions<InstitutionalHoldersSummary, Error, InstitutionalHoldersSummary>
        >(() => ({
            queryKey: getInstitutionalHoldersSummaryCacheKey(this.#symbol.value),
            enabled: false,
            gcTime: SUMMARY_CACHE_TIME,
            staleTime: SUMMARY_CACHE_TIME,
        }));
        this.#summaryQuery = queryObserverModelFactory<InstitutionalHoldersSummary, Error, InstitutionalHoldersSummary>(
            summaryQueryOptions,
        );
        this.#disposableStack.use(this.#summaryQuery);
        this.#disposableStack.use(
            this.#holdersQuery.subscribe((result) => {
                if (result.data === undefined) return;
                queryClient.setQueryData(
                    getInstitutionalHoldersSummaryCacheKey(result.data.symbol),
                    toSummary(result.data),
                );
            }),
        );

        this.#periods = computed(() => {
            const asOfDate = this.#holdersQuery.signal.value.data?.asOfDate ?? null;
            // API gap: the endpoint accepts an asOfDate, but 6.4.2 does not
            // expose available historical as-of dates. Keep one "latest"
            // period until the API provides period discovery.
            return [{ id: 'latest', label: formatLocalDate(asOfDate), asOfDate }];
        });
        this.#loading = computed(() => {
            const result = this.#holdersQuery.signal.value;
            return result.isLoading || result.isFetching;
        });
        this.#summaryLoading = computed(
            () => this.#summaryQuery.signal.value.data === undefined && this.#loading.value,
        );
        this.#error = computed(() => this.#holdersQuery.signal.value.error?.message ?? null);
        this.#summary = computed(() => toSummaryView(this.#summaryQuery.signal.value.data ?? EMPTY_SUMMARY));

        this.#rows = computed(() => {
            const response = this.#holdersQuery.signal.value.data;
            if (response === undefined || response.holders.length === 0) return EMPTY_ROWS;
            const lastPrice = this.#lastPrice.value;
            const maxOwnershipPct = Math.max(...response.holders.map((row) => row.pctSharesOutstanding));
            return response.holders.map((row, index) =>
                toViewRow(row, response.skip + index + 1, maxOwnershipPct, lastPrice),
            );
        });

        this.#isEmpty = computed(() => !this.#loading.value && this.#rows.value.length === 0);
    }

    get periods(): ReadonlySignal<readonly InstitutionalHolderPeriod[]> {
        return this.#periods;
    }

    get periodId(): ReadonlySignal<HoldersPeriodId> {
        return this.#periodId;
    }

    get filter(): ReadonlySignal<HolderFilter> {
        return this.#filter;
    }

    get search(): ReadonlySignal<string> {
        return this.#search;
    }

    get sortColumn(): ReadonlySignal<HolderSortColumn> {
        return this.#sortColumn;
    }

    get sortDirection(): ReadonlySignal<'asc' | 'desc'> {
        return this.#sortDirection;
    }

    get loading(): ReadonlySignal<boolean> {
        return this.#loading;
    }

    get summaryLoading(): ReadonlySignal<boolean> {
        return this.#summaryLoading;
    }

    get error(): ReadonlySignal<string | null> {
        return this.#error;
    }

    get summary(): ReadonlySignal<InstitutionalHoldersSummaryView> {
        return this.#summary;
    }

    get rows(): ReadonlySignal<readonly InstitutionalHolderViewRow[]> {
        return this.#rows;
    }

    get isEmpty(): ReadonlySignal<boolean> {
        return this.#isEmpty;
    }

    setPeriod(periodId: HoldersPeriodId): void {
        this.#state.value = reducer(this.#state.value, setPeriod(periodId));
    }

    setFilter(filter: HolderFilter): void {
        this.#state.value = reducer(this.#state.value, setFilter(filter));
    }

    setSearch(search: string): void {
        this.#state.value = reducer(this.#state.value, setSearch(search));
    }

    setSort(column: HolderSortColumn): void {
        this.#state.value = reducer(this.#state.value, setSort(column));
    }

    refetch(): void {
        void this.#holdersQuery.queryObserver.refetch();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}

export type {
    HolderFilter,
    HolderSortColumn,
    HoldersPeriodId,
    InstitutionalHolderPeriod,
    InstitutionalHolderViewRow,
    InstitutionalHoldersSummaryView,
} from './state/types';
